import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import axiosBaseURL from "..";
import { AuthContextValue, useAuth } from "../../providers/AuthProvider";
import {
  UnitOfMeasurement,
  UnitOfMeasurementPayload,
} from "../../types/unit-of-measurement";

const UOM_KEY = "unit-of-measurement";

function normalizeList(data: unknown): UnitOfMeasurement[] {
  if (Array.isArray(data)) return data;
  if (data && typeof data === "object" && "data" in data) {
    const inner = (data as { data: unknown }).data;
    if (Array.isArray(inner)) return inner;
  }
  return [];
}

export const useGetUnitOfMeasurements = (options?: {
  activeOnly?: boolean;
}) => {
  const { user } = useAuth() as AuthContextValue;
  return useQuery({
    queryKey: [UOM_KEY, options?.activeOnly ?? false],
    enabled: !!user?.token,
    staleTime: 60_000,
    queryFn: async (): Promise<UnitOfMeasurement[]> => {
      const params = new URLSearchParams();
      params.append("activeOnly", String(options?.activeOnly ?? false));
      const { data } = await axiosBaseURL.get(
        `/admin/unit-of-measurement?${params.toString()}`,
        { headers: { header: `${user?.userId}/${user?.token}` } },
      );
      return normalizeList(data);
    },
  });
};

export const useGetUnitOfMeasurementById = (id?: number) => {
  const { user } = useAuth() as AuthContextValue;
  return useQuery({
    queryKey: [UOM_KEY, "byId", id],
    enabled: !!user?.token && !!id,
    queryFn: async (): Promise<UnitOfMeasurement | null> => {
      const { data } = await axiosBaseURL.get<UnitOfMeasurement>(
        `/admin/unit-of-measurement/${id}`,
        { headers: { header: `${user?.userId}/${user?.token}` } },
      );
      return data ?? null;
    },
  });
};

export const useCreateUnitOfMeasurement = () => {
  const { user } = useAuth() as AuthContextValue;
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (
      payload: UnitOfMeasurementPayload,
    ): Promise<UnitOfMeasurement> => {
      const { data } = await axiosBaseURL.post<UnitOfMeasurement>(
        `/admin/unit-of-measurement`,
        payload,
        { headers: { header: `${user?.userId}/${user?.token}` } },
      );
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [UOM_KEY] });
    },
  });
};

export const useUpdateUnitOfMeasurement = () => {
  const { user } = useAuth() as AuthContextValue;
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (args: {
      id: number;
      payload: UnitOfMeasurementPayload;
    }): Promise<UnitOfMeasurement> => {
      const { data } = await axiosBaseURL.put<UnitOfMeasurement>(
        `/admin/unit-of-measurement/${args.id}`,
        args.payload,
        { headers: { header: `${user?.userId}/${user?.token}` } },
      );
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [UOM_KEY] });
    },
  });
};
